import {
  CheckCircle,
  type Icon,
  TrendUp,
  UserSwitch,
  Warning,
  XCircle,
} from '@phosphor-icons/react';
import type {
  NotificationCategory,
  NotificationDto,
  NotificationIcon,
} from '@reelplicate/api-client/model';
import { Avatar, type NotificationEntry } from '@reelplicate/ui';
import { Fragment } from 'react';

const ICONS: Record<NotificationIcon, Icon> = {
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  warning: Warning,
  'trend-up': TrendUp,
  'user-switch': UserSwitch,
};

const CATEGORY: Record<NotificationCategory, string> = {
  FLOW: 'Flow',
  REVIEW: 'Review',
  ANALYTICS: 'Analytics',
  BRAND: 'Brand',
  TEAM: 'Team',
  COMMENT: 'Comment',
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "Just now", "8m ago", "5h ago", "3d ago", then the date. */
export function relativeTime(at: Date, now: Date): string {
  const ago = now.getTime() - at.getTime();
  if (ago < MINUTE) return 'Just now';
  if (ago < HOUR) return `${Math.floor(ago / MINUTE)}m ago`;
  if (ago < DAY) return `${Math.floor(ago / HOUR)}h ago`;
  if (ago < 7 * DAY) return `${Math.floor(ago / DAY)}d ago`;
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/**
 * An API notification as a Notification Center entry: the sentence with its
 * bold names, the tone icon or the actor's avatar, and Today / Earlier by the
 * viewer's local day. The inline action link is added by the caller, which
 * owns navigation.
 */
export function toEntry(
  notification: NotificationDto,
  now: Date,
): Omit<NotificationEntry, 'action'> {
  const at = new Date(notification.occurredAt);
  const { actor, tone, icon } = notification;
  return {
    id: notification.id,
    group: sameDay(at, now) ? 'Today' : 'Earlier',
    unread: notification.readAt === null,
    mention: notification.mention,
    ...(tone && icon
      ? { tone, icon: ICONS[icon] }
      : {
          avatar: (
            <Avatar
              size={32}
              name={actor?.name ?? undefined}
              src={actor?.imageUrl ?? undefined}
              alt=""
            />
          ),
        }),
    message: (
      <>
        {notification.message.map((part, index) =>
          part.strong ? (
            <strong key={index}>{part.text}</strong>
          ) : (
            <Fragment key={index}>{part.text}</Fragment>
          ),
        )}
      </>
    ),
    quote: notification.quote ?? undefined,
    time: relativeTime(at, now),
    dateTime: notification.occurredAt,
    category: CATEGORY[notification.category],
  };
}
